import { useEffect, useState, useMemo, useRef } from "react";
import { Check, ChevronUp } from "lucide-react";
import Lottie, { LottieRefCurrentProps } from "lottie-react";
import { priceTiers, DRINKS_PER_TUBE, type Product } from "@/data/pricing";
import emptyBoxAnimation from "@/assets/Empty_Box.json";
import confettiAnimation from "@/assets/confetti.json";

interface TubeItem {
  id: string;
  productId: string;
  color: string;
}

interface PackageBoxProps {
  quantities: Record<string, number>;
  products: Product[];
  totalQty: number;
  currentTierTubes: number | null;
  celebrating: boolean;
  celebrationKey?: number;
  mode: "purchase" | "subscription";
}

const PRODUCT_COLORS: Record<string, string> = {
  immunsupport: "bg-purple-400",
  "c-vitamin": "bg-orange-400",
  "sol-gladje": "bg-yellow-400",
  elektrolyter: "bg-green-400",
  tropikollagen: "bg-pink-400",
};

const PackageBox = ({ quantities, products, totalQty, currentTierTubes, celebrating, celebrationKey = 0, mode }: PackageBoxProps) => {
  const lottieRef = useRef<LottieRefCurrentProps>(null);
  const [prevQty, setPrevQty] = useState(totalQty);
  const [newTubeIds, setNewTubeIds] = useState<Set<string>>(new Set());
  const [showBadge, setShowBadge] = useState(false);
  const [badgeFadingOut, setBadgeFadingOut] = useState(false);

  useEffect(() => {
    if (celebrating) {
      setShowBadge(true);
      setBadgeFadingOut(false);
    } else if (showBadge) {
      setBadgeFadingOut(true);
      const timer = setTimeout(() => {
        setShowBadge(false);
        setBadgeFadingOut(false);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [celebrating]);

  const tubes: TubeItem[] = useMemo(() => {
    const result: TubeItem[] = [];
    products.forEach((p) => {
      for (let i = 0; i < (quantities[p.id] || 0); i++) {
        result.push({ id: `${p.id}-${i}`, productId: p.id, color: PRODUCT_COLORS[p.id] || "bg-muted" });
      }
    });
    return result;
  }, [quantities, products]);

  useEffect(() => {
    if (totalQty > prevQty) {
      const latestIds = new Set(tubes.slice(prevQty).map((t) => t.id));
      setNewTubeIds(latestIds);
      const timer = setTimeout(() => setNewTubeIds(new Set()), 500);
      return () => clearTimeout(timer);
    }
    setPrevQty(totalQty);
  }, [totalQty, prevQty, tubes]);

  const [hasPlayedIntro, setHasPlayedIntro] = useState(false);
  const [isIntroPlaying, setIsIntroPlaying] = useState(false);

  useEffect(() => {
    const lottie = lottieRef.current;
    if (!lottie) return;
    const onComplete = () => {
      if (!hasPlayedIntro) {
        setHasPlayedIntro(true);
        setIsIntroPlaying(false);
        lottie.goToAndStop(40, true);
      }
    };
    const animationItem = lottie.animationItem;
    if (!animationItem) return;
    animationItem.addEventListener('complete', onComplete);
    return () => animationItem.removeEventListener('complete', onComplete);
  }, [hasPlayedIntro]);

  // Box always stays at frame 40 — no celebration frame changes
  useEffect(() => {
    if (!lottieRef.current || isIntroPlaying) return;
    if (!hasPlayedIntro && totalQty === 0) {
      setIsIntroPlaying(true);
      lottieRef.current.playSegments([0, 40], true);
    } else {
      lottieRef.current.goToAndStop(40, true);
    }
  }, [totalQty, hasPlayedIntro, isIntroPlaying]);

  const totalMax = priceTiers[priceTiers.length - 1].tubes;
  const nextTier = priceTiers.find((t) => t.tubes > totalQty);
  const currentTier = priceTiers.find((t) => t.tubes === totalQty);
  const nearestTier = nextTier || priceTiers[priceTiers.length - 1];
  const maxForLevel = nearestTier.tubes;

  const progressPct = (totalQty / totalMax) * 100;

  const ringSize = 310;
  const ringRadius = ringSize / 2 - 14;
  const circumference = 2 * Math.PI * ringRadius;
  const dashOffset = circumference - (progressPct / 100) * circumference;

  const cx = ringSize / 2;
  const cy = ringSize / 2;
  const milestones = priceTiers.map((t) => {
    const angle = (t.tubes / totalMax) * 2 * Math.PI;
    const x = cx + ringRadius * Math.cos(angle);
    const y = cy + ringRadius * Math.sin(angle);
    const reached = totalQty >= t.tubes;
    const isNext = !reached && nextTier?.tubes === t.tubes;
    return { ...t, x, y, reached, isNext, angle };
  });

  const progressFraction = totalQty / totalMax;
  const progressColor = progressFraction >= 0.75
    ? "#eab308"
    : progressFraction >= 0.4
    ? "hsl(var(--primary))"
    : "hsl(var(--foreground))";

  const totalDrinks = totalQty * DRINKS_PER_TUBE;

  return (
    <div className="flex flex-col items-center gap-6">
      <div className="relative flex items-center justify-center" style={{ width: ringSize, height: ringSize }}>
        <svg
          width={ringSize}
          height={ringSize}
          className="absolute transition-all duration-700"
          style={{ transform: "rotate(-90deg)" }}
        >
          <circle cx={cx} cy={cy} r={ringRadius} fill="none" stroke="hsl(var(--border))" strokeWidth="3" />
          <circle
            cx={cx} cy={cy} r={ringRadius} fill="none"
            stroke={progressColor} strokeWidth="4" strokeLinecap="round"
            strokeDasharray={circumference} strokeDashoffset={dashOffset}
            className="transition-all duration-700 ease-out"
          />
          {milestones.map((m) => {
            const fill = m.reached ? progressColor : "hsl(var(--background))";
            const stroke = m.reached ? progressColor : m.isNext ? "hsl(var(--foreground))" : "hsl(var(--border))";
            return (
              <g key={m.tubes}>
                <circle cx={m.x} cy={m.y} r={5} fill={fill} stroke={stroke} strokeWidth={2}
                  className={`transition-all duration-500 ${m.isNext ? "animate-pulse" : ""}`} />
              </g>
            );
          })}
        </svg>

        <div className={`relative w-64 h-64 md:w-72 md:h-72 flex items-center justify-center -translate-y-5 md:-translate-y-6 transition-all duration-700 ease-out ${celebrating ? "animate-shake" : ""}`}>
          <Lottie
            lottieRef={lottieRef}
            animationData={emptyBoxAnimation}
            loop={false}
            autoplay={false}
            className="absolute inset-0 w-full h-full"
          />

          {/* Confetti overlay */}
          {celebrating && (
            <div key={celebrationKey} className="absolute -inset-4 z-30 pointer-events-none flex items-center justify-center overflow-visible">
              <Lottie
                animationData={confettiAnimation}
                loop={false}
                autoplay={true}
                className="w-full h-full"
              />
            </div>
          )}

          {showBadge && currentTierTubes && (() => {
            const tierInfo = priceTiers.find(t => t.tubes === currentTierTubes);
            if (!tierInfo) return null;
            const basePricePerTube = priceTiers[0].purchasePrice / priceTiers[0].tubes;
            const actualPrice = mode === "purchase" ? tierInfo.purchasePrice : tierInfo.subscriptionPrice;
            const savings = Math.round(basePricePerTube * tierInfo.tubes - actualPrice);
            return (
              <div className={`absolute inset-0 flex items-center justify-center z-20 ${badgeFadingOut ? "animate-fade-out" : "animate-fade-in"}`}>
                <div className="bg-foreground/90 rounded-2xl px-6 py-4 flex flex-col items-center gap-2">
                  <Check className="w-8 h-8 text-background" />
                  <p className="text-background text-xs font-bold uppercase tracking-widest">
                    {savings > 0 ? `Du sparar ${savings} kr!` : "Bra start!"}
                  </p>
                </div>
              </div>
            );
          })()}

          <div className="relative z-10 flex flex-wrap gap-1.5 justify-center items-center p-4 max-w-[80%]">
            {tubes.map((tube) => (
              <div
                key={tube.id}
                className={`w-4 h-4 md:w-5 md:h-5 rounded-full ${tube.color} transition-all duration-500 shadow-sm ${
                  newTubeIds.has(tube.id) ? "animate-scale-in" : ""
                }`}
                style={{ animationTimingFunction: "cubic-bezier(0.34, 1.56, 0.64, 1)" }}
              />
            ))}
            {totalQty < maxForLevel &&
              Array.from({ length: Math.min(maxForLevel - totalQty, 20) }).map((_, i) => (
                <div key={`empty-${i}`}
                  className="w-4 h-4 md:w-5 md:h-5 rounded-full border-2 border-dashed border-foreground/10 transition-all duration-300" />
              ))}
          </div>

          <div className="absolute bottom-2 left-0 right-0 text-center z-10">
            <p className="text-[10px] font-bold uppercase tracking-widest text-foreground/60">
              {totalDrinks} drycker
            </p>
          </div>
        </div>
      </div>

      <div className="text-center space-y-1">
        {currentTier && (
          <p className="text-xs font-bold uppercase tracking-widest text-foreground">
            {currentTier.name} paket
          </p>
        )}
        {nextTier && totalQty > 0 && (
          <p className="text-[10px] text-muted-foreground tracking-wide flex items-center gap-1 justify-center">
            <ChevronUp className="w-3 h-3" />
            {nextTier.tubes - totalQty} till för {nextTier.name}
          </p>
        )}
      </div>
    </div>
  );
};

export default PackageBox;
